import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@lib/utils';
import { useMarket } from '@contexts/MarketContext';

interface NavItem {
  label: string;
  labelBR: string;
  path: string;
  icon: JSX.Element;
  badge?: number;
}

interface NavSection {
  title: string;
  titleBR: string;
  icon: JSX.Element;
  items: NavItem[];
}

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  onCloseMobile?: () => void;
  isMobile?: boolean;
}

/**
 * Sidebar Navigation Component
 * Collapsible sidebar with navigation sections
 * Mobile-optimized with close button
 */
export function Sidebar({ isCollapsed, onToggle, onCloseMobile, isMobile }: SidebarProps): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { isBrazil } = useMarket();
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    'accounts-payable': true,
    'accounts-receivable': true,
  });

  const toggleSection = (section: string): void => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const isActiveSection = (items: NavItem[]): boolean => {
    return items.some((item) => location.pathname.startsWith(item.path));
  };

  const navSections: NavSection[] = [
    {
      title: 'Accounts Payable',
      titleBR: 'Contas a Pagar',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      items: [
        {
          label: 'Bills',
          labelBR: 'Contas',
          path: '/accounts-payable/bills',
          icon: (
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          ),
          badge: 12,
        },
        {
          label: 'Vendors',
          labelBR: 'Fornecedores',
          path: '/accounts-payable/vendors',
          icon: (
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          ),
        },
      ],
    },
    {
      title: 'Accounts Receivable',
      titleBR: 'Contas a Receber',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
      items: [
        {
          label: 'Invoices',
          labelBR: 'Notas Fiscais',
          path: '/accounts-receivable/invoices',
          icon: (
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          ),
          badge: 8,
        },
        {
          label: 'Customers',
          labelBR: 'Clientes',
          path: '/accounts-receivable/customers',
          icon: (
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          ),
        },
      ],
    },
  ];

  return (
    <aside
      className={cn(
        'sticky top-0 flex h-screen flex-col bg-white shadow-card transition-all duration-300',
        isCollapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Logo and Brand - Polished Corporate */}
      <div className="flex h-14 sm:h-16 items-center justify-between border-b border-gray-100 px-3 sm:px-4">
        {(!isCollapsed || isMobile) && (
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 shadow-sm">
              <svg className="h-4 w-4 sm:h-5 sm:w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-sm font-bold text-gray-900">Finova</h1>
              <p className="text-xs font-medium text-teal-600">Financial</p>
            </div>
          </div>
        )}
        <div className="flex items-center gap-2">
          {/* Mobile close button */}
          {isMobile && onCloseMobile && (
            <button
              onClick={onCloseMobile}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-gray-400 hover:bg-teal-50 hover:text-teal-600 transition-all active:scale-95"
              aria-label={isBrazil ? 'Fechar menu' : 'Close menu'}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          {/* Desktop collapse toggle - hidden on mobile */}
          {!isMobile && (
            <button
              onClick={onToggle}
              className="hidden md:flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-teal-50 hover:text-teal-600 transition-all active:scale-95"
              aria-label={isCollapsed ? (isBrazil ? 'Expandir menu' : 'Expand sidebar') : (isBrazil ? 'Recolher menu' : 'Collapse sidebar')}
              title={isCollapsed ? (isBrazil ? 'Expandir menu' : 'Expand sidebar') : (isBrazil ? 'Recolher menu' : 'Collapse sidebar')}
            >
              <svg
                className={cn('h-4 w-4 transition-transform duration-200', isCollapsed && 'rotate-180')}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3">
        {/* Dashboard Link */}
        <NavLink
          to="/dashboard"
          className={({ isActive }) =>
            cn(
              'mb-2 flex items-center gap-3 rounded-xl px-3 py-2.5 transition-all',
              isActive
                ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-sm'
                : 'text-gray-600 hover:bg-teal-50 hover:text-teal-700'
            )
          }
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          {!isCollapsed && <span className="font-medium">{isBrazil ? 'Painel' : 'Dashboard'}</span>}
        </NavLink>

        {/* Reports Link */}
        <NavLink
          to="/reports"
          className={({ isActive }) =>
            cn(
              'mb-2 flex items-center gap-3 rounded-xl px-3 py-2.5 transition-all',
              isActive
                ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-sm'
                : 'text-gray-600 hover:bg-teal-50 hover:text-teal-700'
            )
          }
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          {!isCollapsed && <span className="font-medium">{isBrazil ? 'Relatórios' : 'Reports'}</span>}
        </NavLink>

        {/* Section Divider */}
        {!isCollapsed && <div className="my-4 border-t border-gray-100" />}

        {/* Navigation Sections */}
        {navSections.map((section) => (
          <div key={section.title} className="mb-2">
            <button
              onClick={() => {
                if (isCollapsed) {
                  // When collapsed, navigate to the first item in the section
                  const firstItem = section.items[0];
                  if (firstItem) navigate(firstItem.path);
                } else {
                  toggleSection(section.title.toLowerCase().replace(' ', '-'));
                }
              }}
              className={cn(
                'mb-1 flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left transition-all',
                isActiveSection(section.items)
                  ? 'bg-teal-50 text-teal-700'
                  : 'text-gray-500 hover:bg-teal-50 hover:text-teal-700'
              )}
              title={isCollapsed ? (isBrazil ? section.titleBR : section.title) : undefined}
            >
              <div className="flex items-center gap-3">
                {section.icon}
                {!isCollapsed && <span className="text-sm font-medium">{isBrazil ? section.titleBR : section.title}</span>}
              </div>
              {!isCollapsed && (
                <svg
                  className={cn(
                    'h-4 w-4 transition-transform text-gray-400',
                    expandedSections[section.title.toLowerCase().replace(' ', '-')] && 'rotate-180'
                  )}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </button>

            {!isCollapsed && expandedSections[section.title.toLowerCase().replace(' ', '-')] && (
              <div className="ml-4 space-y-1">
                {section.items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center justify-between rounded-xl px-3 py-2 text-sm transition-all',
                        isActive
                          ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-sm'
                          : 'text-gray-500 hover:bg-teal-50 hover:text-teal-700'
                      )
                    }
                  >
                    <div className="flex items-center gap-2">
                      {item.icon}
                      <span>{isBrazil ? item.labelBR : item.label}</span>
                    </div>
                    {item.badge !== undefined && item.badge > 0 && (
                      <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-xs font-bold text-white">
                        {item.badge}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Footer - App Version */}
      {!isCollapsed && (
        <div className="border-t border-gray-100 p-4">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <p className="text-xs font-medium text-gray-400">Finova Financial v0.1.0</p>
          </div>
        </div>
      )}
    </aside>
  );
}
